// React component

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaGoogle, FaEye, FaEyeSlash, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import logo from '../assets/logo.png';
import EmbeddedBrowserModal from '../components/EmbeddedBrowserModal';
import { isEmbeddedBrowser } from '../lib/firebase';

const Login = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmbeddedBrowserModal, setShowEmbeddedBrowserModal] = useState(false);
  const [isInEmbeddedBrowser, setIsInEmbeddedBrowser] = useState(false);
  const { login, loginWithGoogle, currentUser, authLoading, loading } = useAuth();
  const navigate = useNavigate();

  // Check for embedded browser on mount - fresh check every time, no caching
  useEffect(() => {
    // Force fresh detection - clear any potential cached state
    const checkEmbeddedBrowser = () => {
      const detected = isEmbeddedBrowser();
      setIsInEmbeddedBrowser(detected);
      
      // Auto-show modal if in embedded browser
      if (detected) {
        console.log('[Login] Embedded browser detected - showing modal');
        setShowEmbeddedBrowserModal(true);
      }
    };
    
    // Check immediately
    checkEmbeddedBrowser();
    
    // Also check when window regains focus (in case user switches back)
    window.addEventListener('focus', checkEmbeddedBrowser);
    
    return () => {
      window.removeEventListener('focus', checkEmbeddedBrowser);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('errors.fillAllFields'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(email, password);
      toast.success(t('success.welcomeBack'));
      
      // ProtectedRoute will automatically redirect to dashboard
    } catch (error) {
      console.error('Login error:', error);
      
      // If email not verified, redirect to verify-email page
      if (error.code === 'auth/email-not-verified') {
        toast.error(t('errors.emailNotVerified', 'Please verify your email address before logging in.'));
        navigate('/verify-email');
        return;
      }
      
      let errorMessage = t('errors.loginFailed');
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = t('errors.userNotFound');
          break;
        case 'auth/wrong-password':
          errorMessage = t('errors.wrongPassword');
          break;
        case 'auth/invalid-email':
          errorMessage = t('errors.invalidEmail');
          break;
        case 'auth/too-many-requests':
          errorMessage = t('errors.tooManyRequests');
          break;
        case 'auth/user-disabled':
          errorMessage = t('errors.userDisabled');
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // Always do a fresh check for embedded browser - no caching
    const isEmbedded = isEmbeddedBrowser();
    setIsInEmbeddedBrowser(isEmbedded);
    
    if (isEmbedded) {
      console.log('[Login] Google Sign-In blocked - embedded browser detected');
      setShowEmbeddedBrowserModal(true);
      return;
    }
    
    try {
      await loginWithGoogle();
      // Note: The redirect will handle the success message and navigation
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Show modal for embedded browser error (fallback)
      if (error.code === 'auth/embedded-browser') {
        setIsInEmbeddedBrowser(true);
        setShowEmbeddedBrowserModal(true);
        return;
      }
      
      let errorMessage = t('errors.googleSignInFailed');
      
      switch (error.code) {
        case 'auth/popup-blocked':
          errorMessage = t('errors.popupBlocked');
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = t('errors.popupClosed');
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = t('errors.accountExistsDifferent');
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  return (
    <div dir="ltr" className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#EFF6FF] flex relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-20 w-72 h-72 bg-[#2F80ED]/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#60A5FA]/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Left Side - Branding */}
      <motion.div 
        className="hidden lg:flex lg:w-1/2 bg-white relative overflow-hidden"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 opacity-[0.03]"
            animate={{
              backgroundPosition: ['0px 0px', '80px 80px'],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #2563EB 1px, transparent 1px)',
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        {/* Floating animated dots */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-[#2563EB] rounded-full"
            style={{
              left: `${15 + (i * 12)}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + (i * 0.5),
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.2,
            }}
          />
        ))}

        {/* Larger floating points */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={`large-${i}`}
            className="absolute w-3 h-3 bg-[#2563EB]/30 rounded-full blur-[1px]"
            style={{
              right: `${10 + (i * 15)}%`,
              bottom: `${15 + (i % 4) * 20}%`,
            }}
            animate={{
              y: [0, 40, 0],
              opacity: [0.1, 0.4, 0.1],
              scale: [1, 2, 1],
            }}
            transition={{
              duration: 4 + (i * 0.3),
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
          />
        ))}

        {/* Animated Arrow/Divider - Points to the form */}
        <motion.div 
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20"
          animate={{
            x: [0, -10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="relative">
            {/* Arrow shape */}
            <div className="w-0 h-0 border-t-[30px] border-t-transparent border-l-[40px] border-l-[#2563EB] border-b-[30px] border-b-transparent"></div>
            {/* Glow effect */}
            <motion.div
              className="absolute top-1/2 left-0 -translate-y-1/2 w-12 h-12 bg-[#2563EB]/20 rounded-full blur-xl -z-10"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
        </motion.div>

        {/* Vertical decorative line with animation */}
        <motion.div 
          className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#2563EB]/30 to-transparent"
          animate={{
            opacity: [0.3, 0.8, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <div className="relative z-10 flex flex-col justify-center px-16 text-[#1E293B]">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
          >
            <Link to="/" className="inline-block mb-8 cursor-pointer group">
              <motion.img 
                src={logo} 
                alt="CVLogs Logo" 
                className="w-32 h-32 object-contain"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </Link>
          </motion.div>
          
          <motion.h1 
            className="text-5xl font-bold mb-6 pb-2 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {i18n.language === 'tr' ? 'CVLogs\'a Hoş Geldiniz' : isRTL ? 'مرحباً بك في CVLogs' : 'Welcome to CVLogs'}
          </motion.h1>
          
          <motion.p 
            className="text-xl text-[#64748B] leading-relaxed mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {i18n.language === 'tr' ? "Yapay zeka yardımıyla profesyonel CV'ler oluşturun ve hayalinizdeki işe daha hızlı ulaşın." : isRTL ? "قم بإنشاء سيرة ذاتية احترافية بمساعدة الذكاء الاصطناعي واحصل على وظيفة أحلامك بسرعة." : "Build professional resumes with AI assistance and land your dream job faster."}
          </motion.p>
          
          <div className="space-y-6">
            {[
              { icon: "✓", text: i18n.language === 'tr' ? 'ATS Uyumlu Şablonlar' : isRTL ? 'قوالب متوافقة مع أنظمة تتبع المتقدمين (ATS)' : 'ATS-Optimized Templates', delay: 0.5 },
              { icon: "✓", text: i18n.language === 'tr' ? 'Yapay Zeka Destekli İçerik İyileştirme' : isRTL ? 'تحسين المحتوى باستخدام الذكاء الاصطناعي' : 'AI-Powered Content Enhancement', delay: 0.6 },
              { icon: "✓", text: i18n.language === 'tr' ? 'İş Eşleştirme ve Arama' : isRTL ? 'البحث عن الوظائف والمطابقة الذكية' : 'Job Matching & Search', delay: 0.7 }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="flex items-center space-x-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: item.delay }}
                whileHover={{ x: 10 }}
              >
                <motion.div 
                  className="w-12 h-12 rounded-full bg-[#2563EB]/10 backdrop-blur-sm flex items-center justify-center border-2 border-[#2563EB]/20"
                  whileHover={{ 
                    scale: 1.2, 
                    backgroundColor: "rgba(37, 99, 235, 0.2)",
                    borderColor: "rgba(37, 99, 235, 0.4)",
                    boxShadow: "0 0 20px rgba(37, 99, 235, 0.3)"
                  }}
                >
                  <span className="text-2xl text-[#2563EB]">{item.icon}</span>
                </motion.div>
                <span className="text-lg font-medium text-[#1E293B]">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-16 py-12 relative z-10">
        <motion.div 
          className="w-full max-w-md"
          dir={isRTL ? 'rtl' : 'ltr'}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Mobile Logo */}
          <motion.div 
            className="lg:hidden text-center mb-8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <Link to="/" className="inline-block">
              <motion.img 
                src={logo} 
                alt="CVLogs Logo" 
                className="w-24 h-24 mx-auto mb-4 object-contain"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div 
            className="text-center lg:text-left mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-4xl font-bold text-[#1E293B] mb-2 text-center">
              {t('auth.welcomeBack')}
            </h2>
            <p className="text-[#64748B] text-lg text-center">
              {t('auth.signInDesc')}
            </p>
          </motion.div>

          {/* Login Form */}
          <motion.div 
            className="bg-white/80 backdrop-blur-xl p-8 rounded-2xl shadow-xl border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                  {t('auth.emailAddress')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B]"
                  placeholder={t('auth.enterEmail')}
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B] pr-12"
                    placeholder={t('auth.enterPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#64748B] hover:text-[#2F80ED] transition-colors"
                  >
                    {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-[#2F80ED] hover:text-[#1E40AF] transition-colors"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              {/* Sign In Button */}
              <motion.button
                type="submit"
                disabled={isLoading || authLoading}
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white font-semibold py-3.5 px-4 rounded-xl hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-[#2563EB]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading || authLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  t('auth.signIn')
                )}
              </motion.button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E2E8F0]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-[#64748B] font-medium">{t('auth.orContinueWith')}</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <motion.button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={authLoading}
                className={`w-full font-semibold py-3.5 px-4 rounded-xl focus:outline-none focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center ${
                  isInEmbeddedBrowser 
                    ? 'bg-amber-50 border-2 border-amber-300 text-amber-800 hover:bg-amber-100 focus:ring-amber-200' 
                    : 'bg-white border-2 border-[#E2E8F0] text-[#1E293B] hover:bg-[#F8FAFC] hover:border-[#2563EB] hover:shadow-md focus:ring-[#E2E8F0]'
                }`}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {authLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.connectingGoogle')}
                  </>
                ) : (
                  <>
                    <FaGoogle className={`mr-3 text-lg ${isInEmbeddedBrowser ? 'text-amber-600' : 'text-[#DB4437]'}`} />
                    {isInEmbeddedBrowser ? t('embeddedBrowser.googleNotAvailable', 'Google Sign-In (Open in Browser)') : t('auth.signInGoogle')}
                  </>
                )}
              </motion.button>
            </form>

            {/* Sign Up Link */}
            <motion.div 
              className="text-center pt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <p className="text-[#64748B]">
                {t('auth.noAccount')}{' '}
                <Link
                  to="/register"
                  className="font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                >
                  {t('auth.signUpNow')}
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Embedded Browser Modal */}
      <EmbeddedBrowserModal 
        isOpen={showEmbeddedBrowserModal} 
        onClose={() => setShowEmbeddedBrowserModal(false)} 
      />
    </div>
  );
};

export default Login; 

